import React from "react";
import type { ButtonProps } from "@/components/ui/button";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { usePermissions } from "@/hooks/usePermissions";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";

interface PermissionAwareButtonProps extends ButtonProps {
  requiredPermission?: keyof ReturnType<typeof usePermissions>;
  permissionMessage?: string;
  children: React.ReactNode;
}

export const PermissionAwareButton = React.forwardRef<
  HTMLButtonElement,
  PermissionAwareButtonProps
>(({ requiredPermission, permissionMessage, children, disabled, onClick, ...props }, ref) => {
  const permissions = usePermissions();
  const { t } = useLanguage();
  const descriptionId = React.useId();

  const hasPermission = requiredPermission ? permissions[requiredPermission] : true;
  const isDisabled = disabled || !hasPermission;

  const defaultMessage = permissionMessage || t.common.permissions.defaultMessage;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!hasPermission) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onClick?.(e);
  };

  // A button carrying an EXPLANATION is aria-disabled, never `disabled`: a
  // genuinely disabled button cannot be focused, so the reason it is showing is
  // reachable by mouse only and a keyboard user meets a dead control with no
  // account of where it went. `handleClick` already refuses the action (and
  // stops a Radix trigger wrapping it from firing, since it marks the event
  // handled), so the button is inert without being unreachable. Mirrors
  // `RestrictedActionButton` in the billing domain.
  if (requiredPermission && !hasPermission && permissionMessage) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              {...props}
              ref={ref}
              disabled={false}
              aria-disabled="true"
              aria-describedby={descriptionId}
              className={cn("cursor-not-allowed opacity-50", props.className)}
              onClick={handleClick}
            >
              {children}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{defaultMessage}</p>
          </TooltipContent>
          <span id={descriptionId} className="sr-only">
            {defaultMessage}
          </span>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Button {...props} ref={ref} disabled={isDisabled} onClick={handleClick}>
      {children}
    </Button>
  );
});

PermissionAwareButton.displayName = "PermissionAwareButton";
